import { useDroppable } from '@dnd-kit/core';
import type { VirtualItem } from '@tanstack/react-virtual';
import { cn } from '@tempo-io/sui-class-utils';
import { forwardRef, useContext } from 'react';
import { ColRowHighlightContext, useSwimlaneRootProps, useSwimlaneState } from '../../context';
import { buildCellId } from '../../utils';
import {
  CARD_WIDTH,
  CELL_DRAG_OVER_COLOR,
  COL_ROW_HIGHLIGHT_COLOR,
  COMPACT_CARD_WIDTH,
  SORTABLE_TYPES,
} from '../constants';
import { Cards } from './cards/Cards';

interface ISwimlaneGridCell {
  virtualColumn: VirtualItem;
  virtualRow: VirtualItem;
}

export function SwimlaneGridCell({ virtualColumn, virtualRow }: ISwimlaneGridCell) {
  const { rowHeaders, columnHeaders, isCompact, showGridLines, readOnly } = useSwimlaneRootProps();
  const { getRowHeight } = useSwimlaneState();
  const { colHighlightIndex, rowHighlightIndex } = useContext(ColRowHighlightContext);

  const rowID = rowHeaders[virtualRow.index]?.id ?? 'null';
  const columnID = columnHeaders[virtualColumn.index]?.id ?? '';
  const cellId = buildCellId({ rowID, columnID });
  const cellIdentifier = `cell-${cellId}`;

  const { setNodeRef, isOver } = useDroppable({
    id: cellIdentifier,
    data: {
      type: SORTABLE_TYPES.Cell,
      cellId,
      accepts: [SORTABLE_TYPES.Card],
    },
    disabled: readOnly,
  });

  const isRowGridLineVisible =
    showGridLines && rowHeaders.length > 0 && rowHeaders.length !== virtualRow.index + 1;
  const isColumnGridLineVisible = showGridLines && columnHeaders.length !== virtualColumn.index + 1;

  const isBottomRow = virtualRow.index === rowHeaders.length - 1;
  const isEndColumn = virtualColumn.index === columnHeaders.length - 1;

  const bottomRowExtraStyles = {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  };

  const endColumnExtraStyles = {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  };

  const isColumnHighlighted = colHighlightIndex === virtualColumn.index;
  // styles for highlighting column
  const columnHighLightStyles = {
    background: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    boxShadow: `0px -4px 0px 6px ${COL_ROW_HIGHLIGHT_COLOR}`, // vertically displace shadow by -4px to cover the gaps between cells
    ...(isBottomRow ? bottomRowExtraStyles : {}),
  };

  const isRowHighlighted = rowHighlightIndex === virtualRow.index;
  // styles for highlighting row
  const rowHighLightStyles = {
    background: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    boxShadow: `-4px 0px 0px 6px ${COL_ROW_HIGHLIGHT_COLOR}`, // vertically displace shadow by -4px to cover the gaps between cells
    ...(isEndColumn ? endColumnExtraStyles : {}),
  };

  return (
    <GridCell
      key={virtualColumn.key}
      data-column={virtualColumn.index}
      data-row={virtualRow.index}
      data-testid="grid-cell"
      ref={setNodeRef}
      style={{
        top: `${virtualRow.start}px`,
        left: `${virtualColumn.start}px`,
        minHeight: `${getRowHeight(rowID)}px`,
        minWidth: isCompact ? COMPACT_CARD_WIDTH.label : CARD_WIDTH.label,
        transformStyle: 'preserve-3d',
        background: isOver ? CELL_DRAG_OVER_COLOR : 'transparent',
        ...(isColumnHighlighted ? columnHighLightStyles : {}),
        ...(isRowHighlighted ? rowHighLightStyles : {}),
      }}
      isRowGridLineVisible={isRowGridLineVisible}
      isColumnGridLineVisible={isColumnGridLineVisible}
    >
      {(() => {
        const columnHeader = columnHeaders[virtualColumn.index];
        const rowHeader = rowHeaders[virtualRow.index];
        if (!columnHeader) return null;
        return (
          <Cards cellTop={virtualRow.start} columnHeader={columnHeader} rowHeader={rowHeader} />
        );
      })()}
    </GridCell>
  );
}

// V2: Using Tailwind CSS instead of styled-components
interface GridCellProps extends React.HTMLAttributes<HTMLDivElement> {
  isRowGridLineVisible: boolean;
  isColumnGridLineVisible: boolean;
}

const GridCell = forwardRef<HTMLDivElement, GridCellProps>(
  ({ isRowGridLineVisible, isColumnGridLineVisible, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'absolute left-0 top-0',
          // Row grid line (pseudo-element :after equivalent)
          isRowGridLineVisible && [
            'after:content-[""] after:block after:absolute after:bottom-0 after:h-px',
            'after:left-0 after:right-0 after:-mb-2 after:-ml-2 after:-mr-2',
            'after:border-b after:border-border-neutral-bold/12 after:z-0',
            'after:transform-[translateZ(-1px)]',
          ],
          // Column grid line (pseudo-element :before equivalent)
          isColumnGridLineVisible && [
            'before:content-[""] before:block before:absolute',
            'before:h-[calc(100%+16px)] before:w-px before:left-[calc(100%+8px)]',
            'before:right-0 before:-mr-[9px] before:border-r before:border-border-neutral-bold/12 before:z-0',
          ],
          className,
        )}
        {...props}
      />
    );
  },
);
